import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useReducer,
} from "react";

import { MessageEditorIdContext } from "domains/message-component/components/Compose/components/MessageEditor/MessageEditorIdContext";
import {
    ComposeProviderSettings,
    convertMessageSettingsToMessageState,
} from "domains/message-component/utils/convertMessageSettingsToMessageState";
import { isEqual, omit } from "es-toolkit/compat";

import { COMPOSE_INITIAL_STATE } from "./constants";
import { composeReducer } from "./reducer";
import {
    ComposeActions,
    ComposeContextType,
    ComposeStateType,
    MessageEditorContextType,
} from "./reducer.types";

export const ComposeContext = createContext<ComposeContextType | null>(null);

const isEqualWithExclusions = <T extends object | null | undefined>(
    state1: T,
    state2: T,
    exclusions: (keyof T)[],
) => isEqual(omit(state1, exclusions), omit(state2, exclusions));

export const ComposeStateProvider = ({
    children,
    initialState,
    shouldTryToFetchConsent,
    templatesComboboxMustOpenDownwards,
}: {
    initialState: ComposeStateType;
    shouldTryToFetchConsent?: boolean;
    templatesComboboxMustOpenDownwards?: boolean;
    children: ReactNode;
}) => {
    const [state, dispatch] = useReducer(composeReducer, initialState);

    const contextValue: ComposeContextType = {
        state,
        get isMultipleMessages() {
            return state.messages.length > 1;
        },
        get isComposing() {
            const isComposeStateChanged = !isEqualWithExclusions(
                state,
                initialState,
                [
                    "messages",
                    "isSelfbookConfigurationOpen",
                    "isSaveToRecordEnabled",
                    "contactDetails",
                ],
            );

            return (
                isComposeStateChanged ||
                this.isMultipleMessages ||
                !isEqualWithExclusions(
                    state.messages[0],
                    initialState.messages[0],
                    ["isScheduling"],
                )
            );
        },
        shouldTryToFetchConsent: shouldTryToFetchConsent ?? false,
        dispatch,
        templatesComboboxMustOpenDownwards: templatesComboboxMustOpenDownwards,
    };
    return (
        <ComposeContext.Provider value={contextValue}>
            {children}
        </ComposeContext.Provider>
    );
};

export const ComposeProvider = ({
    children,
    settings,
}: {
    children: ReactNode;
    settings: ComposeProviderSettings;
}) => {
    /**
     * useMemo is important here to avoid re-rendering
     * the ComposeStateProvider on every render
     */
    const initialState: ComposeStateType = useMemo(() => {
        const initialMessage = convertMessageSettingsToMessageState(settings);
        return {
            ...COMPOSE_INITIAL_STATE,
            maxAttachmentCount: settings.maxAttachmentCount,
            originalBody: settings.editableBody,
            originalGreeting: settings.greeting,
            originalSignature: settings.editableSignature,
            canUseQuestionnaires: settings.canUseQuestionnaires,
            conversationParticipant: settings.conversationParticipant,
            contactDetails:
                settings.contactDetails ?? COMPOSE_INITIAL_STATE.contactDetails,
            messages: [initialMessage],
        };
    }, [settings]);

    return (
        <ComposeStateProvider
            initialState={initialState}
            shouldTryToFetchConsent={settings.shouldTryToFetchConsent}
            templatesComboboxMustOpenDownwards={
                settings.templatesComboboxMustOpenDownwards
            }
        >
            {children}
        </ComposeStateProvider>
    );
};

/**
 * Access compose state
 * If used in a message editor context, it'll return the Compose state for that message editor
 * If this is used outside a message editor context, it'll return the Compose state for the first message editor. It's only to help older code (before multiple messages) to work without changes.
 * `dispatch` automatically sets the messageEditorId if it's not provided
 *
 * Use `useComposeMessages` to get the state for all message editors
 */
export const useCompose = (): MessageEditorContextType => {
    const context = useContext(ComposeContext);

    if (context === null) {
        throw new Error("useCompose must be used within an ComposeProvider");
    }

    const { dispatch } = context;

    const messageEditorContext = useContext(MessageEditorIdContext);

    const messageEditorId =
        messageEditorContext === null ? null : messageEditorContext.id;

    /**
     * Memoised context.dispatch function that
     * when called, passes through the current message editor id
     * if it's not already set in the action.
     *
     * When called outside of the MessageEditorIdContext
     * it will dispatch as normal.
     */
    const dispatchWithMessageId = useCallback(
        (action: ComposeActions) => {
            dispatch({
                ...action,
                messageEditorId: action.messageEditorId ?? messageEditorId ?? 0,
            });
        },
        [dispatch, messageEditorId],
    );

    const {
        messages,
        // The state that is shared across all messages
        ...sharedAcrossMessages
    } = context.state;

    return {
        ...context,
        state: {
            ...messages[messageEditorContext?.id ?? 0],
            ...sharedAcrossMessages,
        },
        dispatch: dispatchWithMessageId,
    };
};
